import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import ConfirmModal from "../components/ConfirmModal";
import AlertModal from "../components/AlertModal";
import { useCart } from "../context/CartContext";
import axios from "axios";

const ITEMS_PER_PAGE = 9;

function Build() {
  const navigate = useNavigate();

  const stepTopRef = useRef(null);
  const STICKY_HEADER_OFFSET = 280;

  const [confirmData, setConfirmData] = useState(null);
  const [flowers, setFlowers] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [vips, setVips] = useState([]);


  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [flowersRes, accessoriesRes, vipsRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/products`, { params: { category: "flower" } }),
          axios.get(`${BASE_URL}/api/products`, { params: { category: "accessory" } }),
          axios.get(`${BASE_URL}/api/products`, { params: { category: "vip" } }),
        ]);

        setFlowers(flowersRes.data);
        setAccessories(accessoriesRes.data);
        setVips(vipsRes.data);      // <-- VIPs kept in their own list
      } catch (err) {
        console.error("Error loading products for build page:", err);
      }
    };

    fetchProducts();
  }, [BASE_URL]);


  const {
    addAccessory,
    removeAccessory,
    removeFlower,
    removeVip,
    addVip,
    setFlower,
    addFlower,
    clearCart,
    selectedFlowers,
    buildModeUsedRef,
    buildModeRef,
    exitBuildModeAndFlatten,
    clearAllSelections,
  } = useCart();

  const [step, setStep] = useState(0);
  const [hamperType, setHamperType] = useState(null);
  const [selectedFlower, setSelectedFlower] = useState(null);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [selectedVips, setSelectedVips] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingHamperType, setPendingHamperType] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage] = useState("");

  const [flowerPage, setFlowerPage] = useState(1);
  const [accessoryPage, setAccessoryPage] = useState(1);
  const [vipPage, setVipPage] = useState(1);

  const maxAccessories =
    hamperType === "BlossomDelight" ? 1 :
    hamperType === "CharmingBloom" ? 2 :
    null;

  const paginate = (items, page) => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return items.slice(start, start + ITEMS_PER_PAGE);
  };

  const handleAccessoryToggle = (product) => {
    const exists = selectedAccessories.find((item) => item._id === product._id);

    if (exists) {
      const newAccessories = selectedAccessories.filter((item) => item._id !== product._id);
      setSelectedAccessories(newAccessories);
      removeAccessory(product._id);

      if (hamperType === "CharmingBloom" && newAccessories.length === 1) {
        setConfirmData({
          title: "Switch to BlossomDelight?",
          msg: "You only have one accessory selected. Will you like to switch to Blossom Delight package?",
          onYes: () => setHamperType("BlossomDelight"),
          onCancel: () => {},
        });
      }
      return;
    }

    const total = selectedAccessories.length;

    if (hamperType === "BlossomDelight" && total === 1) {
      setConfirmData({
        title: "Switch to CharmingBloom?",
        msg: "You already selected 1 Accessory. Will you like to switch to Charming Bloom package?",
        onYes: () => {
          setHamperType("CharmingBloom");
          setSelectedAccessories((prev) => [...prev, product]);
          addAccessory(product);
        },
        onCancel: () => {},
      });
      return;
    }

    if (hamperType === "CharmingBloom" && total === 2) {
      setConfirmData({
        title: "Exit Build Mode?",
        msg: "You’ve selected more than 2 accessories. Will you like to exit build mode and continue normal shopping?",
        onYes: () => {
          exitBuildModeAndFlatten({
            flowers: selectedFlower ? [selectedFlower] : [],
            accessories: [...selectedAccessories, product], // keep current picks + the one that triggered exit
            vips: selectedVips,
          });
          navigate("/accessories", { replace: true });
        },
        onCancel: () => {},
      });
      return;
    }

    // Normal add
    setSelectedAccessories((prev) => [...prev, product]);
    addAccessory(product);
  };

  const handleVipToggle = (product) => {
    const exists = selectedVips.find((item) => item._id === product._id);

    if (exists) {
      // Remove VIP if already selected
      const newVips = selectedVips.filter((item) => item._id !== product._id);
      setSelectedVips(newVips);
      removeVip(product._id);
      return;
    }

    const total = selectedVips.length;

    // ✅ Check if user has already selected 5 VIPs
    if (hamperType === "VIPOpulence" && total === 5) {
      setConfirmData({
        title: "Exit Build Mode?",
        msg: "You’ve selected more than 5 VIP items. Would you like to exit build mode and continue normal shopping?",
        onYes: () => {
          exitBuildModeAndFlatten({
            flowers: selectedFlower ? [selectedFlower] : [],
            accessories: selectedAccessories,
            vips: [...selectedVips, product], // keep current picks + the one that triggered exit
          });
          navigate("/vips", { replace: true });
        },
        onCancel: () => {},
      });
      return;
    }

    // Normal add
    setSelectedVips((prev) => [...prev, product]);
    addVip(product);
  };

  useEffect(() => {
    // wait a frame so the step's DOM is in place
    let raf = requestAnimationFrame(() => {
      const el = stepTopRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - STICKY_HEADER_OFFSET;
      window.scrollTo({ top: y, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [step]);


  const handleFlowerSelect = (flower) => {
    if (buildModeRef.current) {
      if (!selectedFlower) {
        setSelectedFlower(flower);
        setFlower(flower);
        return;
      }

      if (selectedFlower && selectedFlower._id === flower._id) {
        removeFlower(flower._id);
        setSelectedFlower(null);
        return; // <-- no setFlower(null)
      }

      setConfirmData({
        title: "Exit Build Mode?",
        msg: "Selecting another flower will exit your current hamper build. Continue?",
        confirmLabel: "Yes, Exit",
        cancelLabel: "Stay Here, But Switch",
        onYes: () => {
          buildModeRef.current = false;
          navigate("/flowers", {
            state: {
              fromBuildExit: true,
              flowers: [selectedFlower, flower],
              accessories: selectedAccessories,
              vips: selectedVips,
            },
          });
        },
        onCancel: () => {
          setSelectedFlower(flower);
          setFlower(flower);
        },
      });
      return;
    }

    // normal shopping (unchanged)
    const alreadySelected = selectedFlowers.some((f) => f._id === flower._id);
    if (alreadySelected) {
      removeFlower(flower._id);
    } else {
      addFlower(flower);
    }
  };

  const handleNext = () => {
    if (step === 2) {
      // ✅ If user picked CharmingBloom but selected only ONE accessory → suggest BlossomDelight instead
      if (hamperType === "CharmingBloom" && selectedAccessories.length === 1) {
        setConfirmData({
          title: "Switch to Blossom Delight?",
          msg:
            "You’ve selected only one accessory. Proceeding will switch your hamper to Blossom Delight.",
          confirmLabel: "Yes, Switch",
          cancelLabel: "Stay with Charming Bloom",
          onConfirm: () => {
            setHamperType("BlossomDelight");
            setStep((prev) => prev + 1);
            setConfirmData(null);
          },
          onClose: () => {
            setConfirmData(null);
          }
        });
        return;
      }

      // ✅ Validate selections based on hamper type
      const isInvalidSelection =
        (hamperType === "BlossomDelight" && selectedAccessories.length !== 1) ||
        (hamperType === "CharmingBloom" && selectedAccessories.length < 1) ||
        (hamperType === "VIPOpulence" && selectedVips.length < 1);

      if (isInvalidSelection) {
        alert("Please make a valid selection before proceeding.");
        return;
      }
    }

    // ✅ Go to next step
    setStep((prev) => prev + 1);
  };

  const handleStartOver = () => {
    clearCart();
    setStep(0);
    setHamperType(null);
    setSelectedFlower(null);
    setSelectedAccessories([]);
    setSelectedVips([]);   // ✅ Explicitly reset VIP items
    setFlowerPage(1);
    setAccessoryPage(1);
    setVipPage(1);         // ✅ Reset VIP pagination
  };

  // Flowers Pagination (unchanged)
  const paginatedFlowers = paginate(flowers || [], flowerPage);
  const flowerTotalPages = Math.ceil((flowers?.length || 0) / ITEMS_PER_PAGE);

  // Normal Accessories Pagination
  const paginatedAccessories = paginate(accessories, accessoryPage);
  const accessoryTotalPages = Math.ceil((accessories?.length || 0) / ITEMS_PER_PAGE);

  // VIP Products Pagination (separate now)
  const paginatedVipProducts = paginate(vips || [], vipPage);
  const vipTotalPages = Math.ceil((vips?.length || 0) / ITEMS_PER_PAGE);


  useEffect(() => {
    const shouldBlockScroll = confirmData || isModalOpen;
    document.body.classList.toggle("overflow-hidden", shouldBlockScroll);

    return () => document.body.classList.remove("overflow-hidden");
  }, [confirmData, isModalOpen]);

  return (
    <>
      <Breadcrumbs />

      <section className="bg-gray-100 py-20 text-center px-4 md:px-6">
        <h1 className="text-4xl font-bold mb-4">Build Your Hamper</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Follow the steps to create the perfect gift combination. Choose a
          flower with 1 accessory - Blossom Delight, 2 accessories - Charming Bloom, or go for VIP collection(s) - VIP Opulence.
        </p>
      </section>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 min-h-[120vh]">
        {/* Step 0: Choose Hamper Type */}
        {step === 0 && (
          <div className="text-center" data-aos="fade-up">
            <h2 className="text-2xl font-bold mb-6">
              Step 1: Choose Hamper Type
            </h2>
            <div className="flex flex-col md:flex-row justify-center gap-4 w-full">
              <button
                onClick={() => { setPendingHamperType("BlossomDelight"); setShowConfirm(true); }}
                className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-full text-center leading-tight"
              >
                Blossom Delight: 1 Flower + 1 Accessory
              </button>

              <button
                onClick={() => { setPendingHamperType("CharmingBloom"); setShowConfirm(true); }}
                className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-full text-center leading-tight"
              >
                Charming Bloom: 1 Flower + 2 Accessories
              </button>

              <button
                onClick={() => { setPendingHamperType("VIPOpulence"); setShowConfirm(true); }}
                className="w-full md:w-auto px-6 py-3 bg-gray-700 text-white rounded-full text-center leading-tight"
              >
                VIP Opulence: 1 Flower + VIPs
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Choose Flower */}
        {step === 1 && (
          <section ref={stepTopRef} data-aos="fade-up">
            <h2 className="text-2xl font-bold text-center mb-6">
              Step 1: Choose a Flower
            </h2>

            {/* Flower List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {paginatedFlowers.map((flower, index) => {
                const isSelected = selectedFlower?._id === flower._id;

                return (
                  <div >
                    <ProductCard
                      product={flower}
                      index={index}
                      onView={() => {
                        setSelectedProduct({ ...flower, type: "flower" });
                        setIsModalOpen(true);
                      }}
                      isBuildMode={true}
                      onBuildAdd={handleFlowerSelect}
                      selected={isSelected}
                    />
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                disabled={flowerPage === 1}
                onClick={() => {
                  setFlowerPage((prev) => prev - 1);
                }}
                className="px-4 py-2 rounded bg-gray-200 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="py-2 px-1">
                Page {flowerPage} of {flowerTotalPages}
              </span>
              <button
                disabled={flowerPage === flowerTotalPages}
                onClick={() => {
                  setFlowerPage((prev) => prev + 1);
                }}
                className="px-4 py-2 rounded bg-gray-200 disabled:opacity-50"
              >
                Next
              </button>
            </div>

            {/* Sticky Navigation Buttons */}
            <div className="
              sticky bottom-0 left-0 w-full bg-white border-t border-gray-200 z-49 mt-4 px-4 py-3
              flex justify-center gap-4
              md:justify-between md:px-10 md:static md:border-0 md:bg-transparent
            ">
              <button
                onClick={() => setStep((prev) => prev - 1)}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 rounded-full whitespace-nowrap"
              >
                Back
              </button>

              <button
                disabled={!selectedFlower}
                onClick={handleNext}
                className={`px-6 py-3 rounded-full text-white whitespace-nowrap ${
                  selectedFlower
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {/* Step 2: Choose Accessories or VIPs */}
        {step === 2 && (
          <section ref={stepTopRef} data-aos="fade-up">
            <h2 className="text-2xl font-bold text-center mb-6">
              {hamperType === "VIPOpulence"
                ? "Step 2: Choose a maximum of 5 VIP Items"
                : `Step 2: Choose ${maxAccessories} Accessor${
                    maxAccessories > 1 ? "ies" : "y"
                  }`}
            </h2>

            {/* Accessories / VIP Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {(hamperType === "VIPOpulence" ? paginatedVipProducts : paginatedAccessories).map((product, index) => {
                const isSelected =
                  hamperType === "VIPOpulence"
                    ? selectedVips.some((item) => item._id === product._id)
                    : selectedAccessories.some((item) => item._id === product._id);

                return (
                  <div>
                    <ProductCard
                      product={product}
                      index={index}
                      isBuildMode={true}
                      selected={isSelected}
                      onView={() => {
                        setSelectedProduct({
                          ...product,
                          type: hamperType === "VIPOpulence" ? "vip" : "accessory",
                        });
                        setIsModalOpen(true);
                      }}
                      onBuildAdd={() => {
                        if (hamperType === "VIPOpulence") {
                          handleVipToggle(product);
                        } else {
                          handleAccessoryToggle(product);
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                disabled={hamperType === "VIPOpulence" ? vipPage === 1 : accessoryPage === 1}
                onClick={() => {
                  if (hamperType === "VIPOpulence") {
                    setVipPage(vipPage - 1);
                  } else {
                    setAccessoryPage(accessoryPage - 1);
                  }
                  //scrollToRef(stepTopRef);
                }}
                className="px-2 py-2 rounded bg-gray-200 disabled:opacity-50"
              >
                Prev
              </button>

              <span className="py-2 px-1">
                Page {hamperType === "VIPOpulence" ? vipPage : accessoryPage} of{" "}
                {hamperType === "VIPOpulence" ? vipTotalPages : accessoryTotalPages}
              </span>

              <button
                disabled={
                  hamperType === "VIPOpulence"
                    ? vipPage === vipTotalPages
                    : accessoryPage === accessoryTotalPages
                }
                onClick={() => {
                  if (hamperType === "VIPOpulence") {
                    setVipPage(vipPage + 1);
                  } else {
                    setAccessoryPage(accessoryPage + 1);
                  }
                }}
                className="px-2 py-2 rounded bg-gray-200 disabled:opacity-50"
              >
                Next
              </button>
            </div>

            {/* Sticky Navigation Buttons */}
            <div className="
              sticky bottom-0 left-0 w-full bg-white border-t border-gray-200 z-49 mt-4 px-4 py-3
              flex justify-center gap-4
              md:justify-between md:px-10 md:static md:border-0 md:bg-transparent
            ">
              <button
                onClick={() => setStep((prev) => prev - 1)}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 rounded-full whitespace-nowrap"
              >
                Back
              </button>

              <button
                onClick={handleNext}
                disabled={
                  (hamperType === "BlossomDelight" && selectedAccessories.length !== 1) ||
                  (hamperType === "VIPOpulence" && selectedVips.length === 0) ||
                  (hamperType === "CharmingBloom" &&
                    selectedAccessories.length !== maxAccessories)
                }
                className={`px-6 py-3 rounded-full text-white whitespace-nowrap ${
                  (hamperType === "BlossomDelight" && selectedAccessories.length !== 1) ||
                  (hamperType === "VIPOpulence" && selectedVips.length === 0) ||
                  (hamperType === "CharmingBloom" &&
                    selectedAccessories.length !== maxAccessories)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {/* Step 3: Hamper Build Success Page */}
        {step === 3 && (
          <section ref={stepTopRef} className="text-center space-y-6" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-green-600">Hamper Build Complete!</h2>
            <p className="text-gray-700">
              You’ve built a <strong>{hamperType}</strong> hamper.
            </p>

            <div className="border p-4 rounded bg-blue-50 max-w-md mx-auto text-left">
              {/* Always show selected flowers if any */}
              {selectedFlowers.length > 0 && (
                <div>
                  <h4 className="font-semibold">Flower:</h4>
                  <p>{selectedFlowers[0].name}</p>
                </div>
              )}

              {/* Show normal accessories for BlossomDelight & CharmingBloom */}
              {(hamperType === "BlossomDelight" || hamperType === "CharmingBloom") &&
                selectedAccessories.length > 0 && (
                  <div className="mt-3">
                    <h4 className="font-semibold">Accessories:</h4>
                    <ul className="list-disc ml-5">
                      {selectedAccessories.map((a) => (
                        <li key={a._id}>{a.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Show VIP accessories for VIPOpulence */}
              {hamperType === "VIPOpulence" && selectedVips.length > 0 && (
                <div className="mt-3">
                  <h4 className="font-semibold">VIP Items:</h4>
                  <ul className="list-disc ml-5">
                    {selectedVips.map((v) => (
                      <li key={v._id}>{v.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                buildModeRef.current = false;
                buildModeUsedRef.current = true;
                navigate("/cart");
              }}
              className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-full hover:bg-blue-700"
            >
              Proceed to Cart
            </button>
          </section>
        )}

      </div>

      {/* Product Modal */}
      {isModalOpen && (
        <ProductModal
          buildMode
          isOpen={isModalOpen}
          product={selectedProduct}
          onBuildFlowerSelect={handleFlowerSelect}
          onBuildAccessorySelect={handleAccessoryToggle}
          onBuildVipSelect={handleVipToggle}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {showConfirm && (
        <ConfirmModal
          isOpen={showConfirm}
          title="Start Fresh?"
          message="Entering build mode will clear all previous selections. Do you want to continue?"
          confirmLabel="Yes, Start Fresh"
          onConfirm={() => {
            clearAllSelections();
            setHamperType(pendingHamperType);
            setStep(1);                  // ✅ now included
            buildModeRef.current = true;

            setShowConfirm(false);
            setPendingHamperType(null);
          }}
          onClose={() => {
            setShowConfirm(false);
            setPendingHamperType(null);
          }}
        />
      )}

      {/* Alert Modal */}
      {showAlert && (
        <AlertModal
          message={alertMessage}
          onClose={() => setShowAlert(false)}
        />
      )}

      {/* Reset Confirmation */}
      {showConfirmReset && (
        <ConfirmModal
          title="Start Over?"
          message="Are you sure you want to reset this build?"
          onConfirm={() => {
            setShowConfirmReset(false);
            handleStartOver();
          }}
          onCancel={() => setShowConfirmReset(false)}
        />
      )}

      {confirmData && (
        <ConfirmModal
          title={confirmData.title}
          message={confirmData.msg}
          onConfirm={() => {
            confirmData.onYes();
            setConfirmData(null); // close after handling
          }}
          onClose={() => {
            confirmData.onCancel?.(); // optional fallback
            setConfirmData(null);
          }}
          confirmLabel={confirmData.confirmLabel}
          cancelLabel={confirmData.cancelLabel}
        />
      )}

      <Footer />
    </>
  );
}

export default Build;
